import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { toast } from 'sonner';

const RequestForm = ({ onSubmit, onSaveDraft, isSubmitting = false }) => {
  const [characterCount, setCharacterCount] = useState(0);
  const maxCharacters = 2000;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      caseType: '',
      claimantName: '',
      respondentName: '',
      subject: '',
      description: '',
    },
  });

  const description = watch('description');

  useEffect(() => {
    setCharacterCount(description?.length || 0);
  }, [description]);

  const caseTypeOptions = [
    {
      value: 'judicial_statement',
      label: 'اظهارنامه قضایی',
      description: 'برای ارائه اطلاعات و مدارک به دادگاه',
    },
    {
      value: 'complaint',
      label: 'شکایت',
      description: 'برای طرح ادعا علیه شخص یا نهاد دیگر',
    },
    {
      value: 'petition',
      label: 'لایحه',
      description: 'برای پاسخ به شکایت یا ارائه دفاعیات',
    },
  ];

  const handleFormSubmit = (data) => {
    if (characterCount < 50) {
      toast?.error('توضیحات باید حداقل ۵۰ کاراکتر باشد');
      return;
    }
    onSubmit(data);
  };

  const handleSaveDraft = () => {
    const formData = watch();
    onSaveDraft(formData);
    toast?.success('پیش‌نویس با موفقیت ذخیره شد');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Case Type Selection */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2 space-x-reverse">
            <Icon name="Scale" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">
              اطلاعات پرونده
            </h2>
          </div>

          <Select
            label="نوع پرونده"
            description="نوع سند حقوقی مورد نیاز خود را انتخاب کنید"
            options={caseTypeOptions}
            value={watch('caseType')}
            onChange={(value) => setValue('caseType', value)}
            error={errors?.caseType?.message}
            required
            placeholder="انتخاب کنید..."
          />
        </div>

        {/* Parties Information */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2 space-x-reverse">
            <Icon name="Users" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">
              اطلاعات طرفین
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="نام و نام خانوادگی شاکی/خواهان"
              type="text"
              placeholder="مثال: احمد محمدی"
              {...register('claimantName', {
                required: 'نام شاکی/خواهان الزامی است',
                minLength: {
                  value: 2,
                  message: 'نام باید حداقل ۲ کاراکتر باشد',
                },
              })}
              error={errors?.claimantName?.message}
              required
            />

            <Input
              label="نام و نام خانوادگی مشکوالیه/خوانده"
              type="text"
              placeholder="مثال: علی احمدی"
              {...register('respondentName', {
                required: 'نام مشکوالیه/خوانده الزامی است',
                minLength: {
                  value: 2,
                  message: 'نام باید حداقل ۲ کاراکتر باشد',
                },
              })}
              error={errors?.respondentName?.message}
              required
            />
          </div>
        </div>

        {/* Case Details */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2 space-x-reverse">
            <Icon name="FileText" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">
              جزئیات پرونده
            </h2>
          </div>

          <Input
            label="موضوع پرونده"
            type="text"
            placeholder="مثال: مطالبه وجه بابت خرید کالا"
            description="خلاصه‌ای از موضوع اصلی پرونده"
            {...register('subject', {
              required: 'موضوع پرونده الزامی است',
              minLength: {
                value: 5,
                message: 'موضوع باید حداقل ۵ کاراکتر باشد',
              },
            })}
            error={errors?.subject?.message}
            required
          />

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              توضیحات تکمیلی
              <span className="text-destructive mr-1">*</span>
            </label>
            <textarea
              {...register('description', {
                required: 'توضیحات الزامی است',
                minLength: {
                  value: 50,
                  message: 'توضیحات باید حداقل ۵۰ کاراکتر باشد',
                },
              })}
              className="w-full min-h-32 px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
              placeholder="شرح کاملی از وقایع، تاریخ‌ها، مبالغ و سایر جزئیات مهم پرونده را بنویسید..."
              maxLength={maxCharacters}
            />

            <div className="flex justify-between items-center text-sm">
              <span
                className={`${
                  characterCount < 50
                    ? 'text-destructive'
                    : characterCount > maxCharacters * 0.9
                    ? 'text-warning'
                    : 'text-muted-foreground'
                }`}>
                {characterCount} / {maxCharacters} کاراکتر
              </span>
              {characterCount < 50 && (
                <span className="text-destructive text-xs">
                  حداقل ۵۰ کاراکتر مورد نیاز است
                </span>
              )}
            </div>

            {errors?.description && (
              <p className="text-sm text-destructive mt-1">
                {errors?.description?.message}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
          <Button
            type="submit"
            variant="default"
            loading={isSubmitting}
            disabled={!isValid || characterCount < 50}
            iconName="Send"
            iconPosition="left"
            className="flex-1 sm:flex-none">
            ارسال درخواست
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            iconName="Save"
            iconPosition="left"
            className="flex-1 sm:flex-none">
            ذخیره پیش‌نویس
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={() => window.history?.back()}
            iconName="ArrowRight"
            iconPosition="left"
            className="flex-1 sm:flex-none">
            بازگشت
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RequestForm;
